import React, {useEffect, useState} from 'react'
import {
  Button,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
  Tooltip,
  Divider,
  MenuItem,
  Select
} from '@mui/material'
import {Delete, Edit, Info, Sort, AccountCircle} from '@mui/icons-material'
import {FaSteam} from 'react-icons/fa'
import {useTranslation} from 'react-i18next'

import RemoveConnectedSteamAccount from './modals/RemoveConnectedSteamAccount'
import EditTradeUrlModal from './modals/EditTradeUrlModal'

import api from '../../../../api/axios'

import Lottie from 'lottie-react'
import loadingAnimation from '../../../../assets/lottie/loading.json'
import {useSearchParams} from 'react-router-dom'
import {toast} from 'react-toastify'

type SteamAccount = {
  id: number
  steamId: string
  profileName: string
  profileSteamUrl: string
  avatarUrl: string
  tradeUrl?: string | null
  creationDate: string
  deleted: boolean
  escrow: boolean
  escrowDays: number | null
  isValid: boolean | null
  status: string | null
}

const ConnectAccount = () => {
  const {t} = useTranslation()

  const [steamAccounts, setSteamAccounts] = useState<SteamAccount[]>([])
  const [deleteOpen, setDeleteOpen] = useState(false)
  const [loading, setLoading] = useState(false)

  const [tradeUrlModalOpen, setTradeUrlModalOpen] = useState(false)
  const [selectedSteamAccount, setSelectedSteamAccount] = useState<SteamAccount | null>(null)

  const [page, setPage] = useState(0)
  const [rowsPerPage, setRowsPerPage] = useState(10)
  const [totalCount, setTotalCount] = useState(0)

  const [sortBy, setSortBy] = useState('profileName')
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc')

  const [searchParams] = useSearchParams()
  const error = searchParams.get('error')

  useEffect(() => {
    if (error) {
      toast.error(decodeURIComponent(error))
    }
  }, [error])

  const API_BASE = process.env.REACT_APP_API_BASE ?? 'http://localhost:8080/api'

  const fetchUsers = async () => {
    setLoading(true)
    try {
      const response = await api.get(`/profile/connected-accounts`, {
        params: {
          sort: `${sortBy},${sortDirection}`,
          page,
          size: rowsPerPage
        }
      })

      setSteamAccounts(response.data.content)
      setTotalCount(response.data.totalElements)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchUsers()
  }, [page, rowsPerPage, sortBy, sortDirection])

  const handleChangePage = (_: unknown, newPage: number) => {
    setPage(newPage)
  }

  const handleChangeRowsPerPage = (event: any) => {
    setRowsPerPage(parseInt(event.target.value, 10))
    setPage(0)
  }

  const handleRequestSort = (property: string) => {
    if (sortBy === property) {
      setSortDirection(prev => (prev === 'asc' ? 'desc' : 'asc'))
    } else {
      setSortBy(property)
      setSortDirection('asc')
    }
  }

  const handleOpenDelete = (user: SteamAccount) => {
    setSelectedSteamAccount(user)
    setDeleteOpen(true)
  }

  const handleConfirmDelete = () => {
    if (selectedSteamAccount) {
      setSteamAccounts(prev => prev.filter(u => u.id !== selectedSteamAccount.id))
    }
    setDeleteOpen(false)
  }

  const handleTradeUrlSaved = () => {
    fetchUsers()
    setTradeUrlModalOpen(false)
  }

  const openSteamLink = (url: string) => {
    window.open(url, '_blank')
  }

  const handleAddSteamAccount = () => {
    const redirectTo = `${window.location.origin}/profile/conntect-account`
    window.location.href = `${API_BASE}/auth/link-another-steam-account?redirect=${encodeURIComponent(redirectTo)}`
  }

  return (
      <div className="flex flex-col md:p-4 p-2 bg-white dark:bg-neutral-900/40 border border-gray-100 dark:border-white/5 min-h-[88vh]">
        <div className="flex flex-col xl:flex-row justify-between items-center gap-4 mb-4 shrink-0">
          <h2 className="text-lg font-bold text-gray-800 dark:text-white uppercase tracking-tighter self-start xl:self-center">
            {t('profile.steamAccount.title')}
          </h2>

          <div className="flex flex-row w-full md:max-w-4xl gap-3 justify-between md:justify-end items-center">
            <div className="text-[10px] font-black uppercase tracking-widest text-gray-400">
              {t('profile.steamAccount.limit')}: {totalCount}/10
            </div>
            <Button
                variant="contained"
                color="primary"
                onClick={handleAddSteamAccount}
                className="h-[36px] whitespace-nowrap flex items-center gap-2 text-xs font-bold px-4"
                sx={{borderRadius: 0}}
                startIcon={<FaSteam className="w-4 h-4"/>}
            >
              {t('profile.steamAccount.connect')}
            </Button>
          </div>
        </div>

        <Divider className="!border-gray-100 dark:!border-white/5" />

        {loading && steamAccounts.length === 0 ? (
            <div className="flex-1 flex justify-center items-center">
              <Lottie animationData={loadingAnimation} loop className="w-64"/>
            </div>
        ) : (
            <TableContainer component={Paper} className="bg-transparent shadow-none border-none mt-4 overflow-hidden" sx={{ borderRadius: 0 }}>
              <Table size="small" className="border-separate border-spacing-y-2">
                <TableHead>
                  <TableRow className="bg-gray-50/50 dark:bg-white/[0.02]">
                    <TableCell sx={{ border: 'none', borderRadius: 0 }} className="text-[10px] font-black text-gray-400 uppercase tracking-widest">
                      <TableSortLabel
                          active={sortBy === 'profileName'}
                          direction={sortBy === 'profileName' ? sortDirection : 'asc'}
                          onClick={() => handleRequestSort('profileName')}
                          IconComponent={Sort}
                      >
                        {t('profile.steamAccount.accountName')}
                      </TableSortLabel>
                    </TableCell>

                    <TableCell sx={{ border: 'none', borderRadius: 0 }} className="text-[10px] font-black text-gray-400 uppercase tracking-widest">Trade-URL</TableCell>
                    <TableCell sx={{ border: 'none', borderRadius: 0 }} className="text-[10px] font-black text-gray-400 uppercase tracking-widest">{t('profile.steamAccount.status')}</TableCell>

                    <TableCell sx={{ display: { xs: 'none', xl: 'table-cell' }, border: 'none', borderRadius: 0 }} className="text-[10px] font-black text-gray-400 uppercase tracking-widest">
                      <TableSortLabel
                          active={sortBy === 'creationDate'}
                          direction={sortBy === 'creationDate' ? sortDirection : 'asc'}
                          onClick={() => handleRequestSort('creationDate')}
                          IconComponent={Sort}
                      >
                        {t('profile.steamAccount.addedAt')}
                      </TableSortLabel>
                    </TableCell>

                    <TableCell align="right" sx={{ border: 'none', borderRadius: 0 }} className="text-[10px] font-black text-gray-400 uppercase tracking-widest">{t('profile.steamAccount.actions')}</TableCell>
                  </TableRow>
                </TableHead>

                <TableBody>
                  {steamAccounts.map((account) => (
                      <TableRow
                          key={account.steamId}
                          className={`group transition-all duration-200 ${account.deleted ? 'bg-red-500/10' : 'bg-gray-50/30 dark:bg-neutral-900/20 hover:bg-gray-100/50 dark:hover:bg-white/[0.03]'}`}
                      >
                        <TableCell sx={{ border: 'none', borderRadius: 0 }} onClick={() => openSteamLink(account.profileSteamUrl)}>
                          <Tooltip title={t('profile.steamAccount.steamProfileTooltip')} arrow>
                            <div className="flex items-center gap-3 cursor-pointer">
                              <img src={account.avatarUrl} className="w-10 h-10 border border-gray-200 dark:border-white/10" alt="avatar"/>
                              <div>
                                <div className="font-bold text-gray-700 dark:text-gray-200 text-xs sm:text-sm hover:underline">{account.profileName}</div>
                                <div className="text-[10px] text-gray-400 font-mono italic">{account.steamId}</div>
                              </div>
                            </div>
                          </Tooltip>
                        </TableCell>

                        <TableCell sx={{ border: 'none', borderRadius: 0 }}>
                          <div className="flex flex-col gap-1.5">
                            <Button
                                size="small"
                                variant="contained"
                                startIcon={account.tradeUrl ? <Edit sx={{ fontSize: 14 }}/> : <FaSteam/>}
                                className="!h-7 !text-[10px] !font-bold !uppercase !tracking-tighter !shadow-none !w-fit"
                                sx={{
                                  borderRadius: 0,
                                  backgroundColor: account.tradeUrl ? 'rgba(234, 179, 8, 0.1)' : 'rgba(59, 130, 246, 0.1)',
                                  color: account.tradeUrl ? '#eab308' : '#3b82f6',
                                  '&:hover': { backgroundColor: account.tradeUrl ? 'rgba(234, 179, 8, 0.2)' : 'rgba(59, 130, 246, 0.2)' }
                                }}
                                onClick={() => {
                                  setSelectedSteamAccount(account)
                                  setTradeUrlModalOpen(true)
                                }}
                            >
                              {account.tradeUrl ? 'Edit URL' : 'Add URL'}
                            </Button>

                            {account.tradeUrl && (
                                <div className="flex items-center gap-2">
                                  <span className={`text-[10px] px-1.5 py-0.5 font-bold uppercase tracking-tighter border ${account.isValid ? 'bg-emerald-500/10 text-emerald-500 border-emerald-500/20' : 'bg-red-500/10 text-red-500 border-red-500/20'}`}>
                                    {account.isValid ? 'Valid' : 'Invalid'}
                                  </span>
                                  {account.escrow && (
                                      <Tooltip title="We only accept trade url with 0 escrow days" arrow>
                                        <Info className="text-gray-400 w-3.5 h-3.5 cursor-pointer"/>
                                      </Tooltip>
                                  )}
                                </div>
                            )}
                          </div>
                        </TableCell>

                        <TableCell sx={{ border: 'none', borderRadius: 0 }}>
                          {account.deleted ? (
                              <span className="text-[10px] px-2 py-0.5 font-bold uppercase tracking-tighter border bg-red-500/10 text-red-500 border-red-500/20">{t('profile.steamAccount.banned')}</span>
                          ) : !account.tradeUrl ? (
                              <div className="flex flex-col">
                                <span className="text-[10px] font-bold uppercase text-yellow-500">{t('profile.steamAccount.missingTradeUrl')}</span>
                                <span className="text-[9px] text-gray-400 uppercase leading-tight">{t('profile.steamAccount.missingTradeUrlDesc')}</span>
                              </div>
                          ) : account.isValid === false ? (
                              <div className="flex flex-col">
                                <span className="text-[10px] font-bold uppercase text-red-500">{t('profile.steamAccount.invalidTradeUrl')}</span>
                                <span className="text-[9px] text-gray-400 uppercase leading-tight">
                                  {account.escrow ? `Escrow: ${account.escrowDays} Days` : t('profile.steamAccount.invalidTradeUrlDesc')}
                                </span>
                              </div>
                          ) : account.status !== 'INSTANT' ? (
                              <div className="flex flex-col">
                                <span className="text-[10px] font-bold uppercase text-orange-500">{t('profile.steamAccount.noInstantTrades')}</span>
                                <span className="text-[9px] text-gray-400 uppercase leading-tight">{t('profile.steamAccount.noInstantTradesDesc')}</span>
                              </div>
                          ) : (
                              <span className="text-[10px] px-2 py-0.5 font-bold uppercase tracking-tighter border bg-emerald-500/10 text-emerald-500 border-emerald-500/20">{t('profile.steamAccount.ok')}</span>
                          )}
                        </TableCell>

                        <TableCell sx={{ display: { xs: 'none', xl: 'table-cell' }, border: 'none', borderRadius: 0 }} className="text-[10px] text-gray-400 font-medium">
                          {new Date(account.creationDate).toLocaleString('de-DE')}
                        </TableCell>

                        <TableCell align="right" sx={{ border: 'none', borderRadius: 0 }}>
                          <div className="flex justify-end gap-1 opacity-80 group-hover:opacity-100 transition-opacity">
                            <Tooltip title={t('profile.steamAccount.disconnectTooltip')} arrow>
                              <IconButton
                                  size="small"
                                  disableRipple
                                  className="!bg-transparent hover:!bg-red-500/10 transition-all"
                                  sx={{ borderRadius: 0 }}
                                  onClick={() => handleOpenDelete(account)}
                              >
                                <div className="p-1.5 bg-red-500/10 flex items-center justify-center">
                                  <Delete sx={{ fontSize: 16 }} className="text-red-500" />
                                </div>
                              </IconButton>
                            </Tooltip>
                          </div>
                        </TableCell>
                      </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
        )}

        <div className="flex flex-col md:flex-row justify-between items-center gap-4 pt-4 border-t border-gray-100 dark:border-white/5 mt-auto shrink-0">
          <div className="flex items-center gap-3">
            <span className="text-[10px] text-gray-400 font-bold uppercase">Rows</span>
            <Select
                value={rowsPerPage}
                onChange={handleChangeRowsPerPage}
                size="small"
                className="bg-gray-50 dark:bg-neutral-800/50 text-gray-600 dark:text-gray-300 !text-[10px] h-7"
                sx={{ '.MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(0,0,0,0.05)', borderRadius: 0 } }}
            >
              {[10, 20, 50].map(s => <MenuItem key={s} value={s} className="!text-xs">{s}</MenuItem>)}
            </Select>
          </div>
          <TablePagination
              component="div"
              count={totalCount}
              page={page}
              onPageChange={handleChangePage}
              rowsPerPage={rowsPerPage}
              rowsPerPageOptions={[]}
              sx={{
                border: 'none',
                '& .MuiTablePagination-actions': {
                  '& .MuiButtonBase-root': {
                    backgroundColor: 'rgba(59, 130, 246, 0.1)',
                    color: '#3b82f6',
                    borderRadius: 0,
                    margin: '0 4px'
                  }
                }
              }}
          />
          <div className="text-[10px] text-gray-400 font-mono">{totalCount} total</div>
        </div>

        <EditTradeUrlModal
            open={tradeUrlModalOpen}
            steamAccountId={selectedSteamAccount?.id}
            steamAccountSteamId={selectedSteamAccount?.steamId}
            steamName={selectedSteamAccount?.profileName}
            avatarUrl={selectedSteamAccount?.avatarUrl}
            tradeUrl={selectedSteamAccount?.tradeUrl ?? ''}
            onClose={() => setTradeUrlModalOpen(false)}
            onSaved={handleTradeUrlSaved}
        />

        <RemoveConnectedSteamAccount
            open={deleteOpen}
            id={selectedSteamAccount?.id}
            steamUserName={selectedSteamAccount?.profileName}
            onClose={() => setDeleteOpen(false)}
            onDeleted={handleConfirmDelete}
        />
      </div>
  )
}

export default ConnectAccount